import axios from "axios";
import { auth, getAppCheckToken } from "@/service/firebase";
import { managementApiBaseUrl } from "@/service/totemDeviceConfig";
const PREFIX = "photonow.event-attendance.v1:";
const client = () =>
  axios.create({ baseURL: managementApiBaseUrl(), timeout: 15000 });
export function eventRequestId() {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const b = new Uint8Array(16);
  window.crypto.getRandomValues(b);
  b[6] = (b[6] & 15) | 64;
  b[8] = (b[8] & 63) | 128;
  const h = Array.from(b, (n) => n.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(
    16,
    20
  )}-${h.slice(20)}`;
}
function assertContext(id, uid) {
  if (!id || !uid || auth?.currentUser?.uid !== uid)
    throw new Error("Sessão do Totem ausente.");
}
function sessionKey(id, uid = auth?.currentUser?.uid) {
  assertContext(id, uid);
  return PREFIX + uid + ":" + id;
}
export function assertEventSession(id, session) {
  assertContext(id, session?.uid);
  if (session.totemId !== id) throw new Error("Atendimento de outro Totem.");
}
export function currentAttendance(id) {
  if (!id || !auth?.currentUser?.uid) return null;
  const raw = localStorage.getItem(sessionKey(id));
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw);
    if (
      saved.totemId === id &&
      saved.uid === auth.currentUser.uid &&
      saved.attendanceId &&
      saved.reservationToken
    )
      return saved;
  } catch (_) {
    throw new Error("Atendimento local inválido. Peça ajuda ao organizador.");
  }
  throw new Error("Atendimento local inválido. Peça ajuda ao organizador.");
}
export function saveAttendance(
  id,
  value,
  uid = value.uid || auth?.currentUser?.uid
) {
  assertContext(id, uid);
  if (value.totemId && value.totemId !== id)
    throw new Error("Atendimento de outro Totem.");
  const saved = { ...value, uid, totemId: id };
  localStorage.setItem(sessionKey(id, uid), JSON.stringify(saved));
  return saved;
}
export function clearAttendance(id, uid = auth?.currentUser?.uid) {
  localStorage.removeItem(sessionKey(id, uid));
}
export function pendingEventCode(id) {
  if (!id || !auth?.currentUser?.uid) return null;
  const raw = localStorage.getItem(sessionKey(id) + ":pending");
  return raw ? JSON.parse(raw) : null;
}
export function eventCodesRequired(id) {
  if (!id || !auth?.currentUser?.uid) return false;
  return localStorage.getItem(sessionKey(id) + ":enabled") === "true";
}
async function request(
  id,
  method,
  suffix,
  data,
  extraHeaders = {},
  uid = auth?.currentUser?.uid
) {
  assertContext(id, uid);
  const user = auth.currentUser;
  const [token, appCheck] = await Promise.all([
    user.getIdToken(),
    getAppCheckToken(),
  ]);
  assertContext(id, uid);
  const response = await client().request({
    method,
    url: `/totem/${encodeURIComponent(id)}/codigos-evento${suffix}`,
    data,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(appCheck ? { "X-Firebase-AppCheck": appCheck } : {}),
      ...extraHeaders,
    },
  });
  assertContext(id, uid);
  return response.data;
}
export async function fetchEventConfig(id) {
  const uid = auth?.currentUser?.uid;
  const config = await request(id, "get", "/config", undefined, {}, uid);
  if (config.totemId !== id || typeof config.enabled !== "boolean")
    throw new Error("Configuração de Evento inválida.");
  localStorage.setItem(
    sessionKey(id, uid) + ":enabled",
    String(config.enabled)
  );
  return config;
}
export async function reserveEventCode(id, code, requestId) {
  const uid = auth?.currentUser?.uid;
  const key = sessionKey(id, uid);
  localStorage.setItem(key + ":pending", JSON.stringify({ code, requestId }));
  let result;
  try {
    result = await request(
      id,
      "post",
      "/reservas",
      { code, requestId },
      {},
      uid
    );
  } catch (error) {
    if ([400, 403, 404, 409, 422].includes(error?.response?.status))
      localStorage.removeItem(key + ":pending");
    throw error;
  }
  // Persist before navigating/creating purchase. A storage failure never submits printing.
  const saved = saveAttendance(id, result, uid);
  localStorage.removeItem(key + ":pending");
  return saved;
}
export function eventAttendanceStatus(id, session = currentAttendance(id)) {
  if (!session) throw new Error("Código não autorizado.");
  assertEventSession(id, session);
  return request(
    id,
    "get",
    `/atendimentos/${encodeURIComponent(session.attendanceId)}`,
    undefined,
    { "x-photonow-reservation-token": session.reservationToken },
    session.uid
  );
}
export async function releaseAttendance(id) {
  const session = currentAttendance(id);
  if (!session) return;
  assertEventSession(id, session);
  await request(
    id,
    "post",
    `/atendimentos/${encodeURIComponent(session.attendanceId)}/release`,
    { reservationToken: session.reservationToken },
    {},
    session.uid
  );
  clearAttendance(id, session.uid);
}
export async function freezeEventPlan(id, session, job) {
  assertEventSession(id, session);
  const jobs = Array.isArray(job) ? job : [job];
  if (!jobs.length || jobs.length > 20 || new Set(jobs.map((item) => item.jobId)).size !== jobs.length)
    throw new Error("Plano do atendimento inválido.");
  await request(
    id,
    "post",
    `/atendimentos/${encodeURIComponent(session.attendanceId)}/plano`,
    { reservationToken: session.reservationToken, jobs },
    {},
    session.uid
  );
  return saveAttendance(id, { ...session, plan: jobs[0], jobs }, session.uid);
}
export function isMonitorCompleted(local, session) {
  const plan = session?.plan;
  if (!plan || !session.attendanceId || !session.eventId || !plan.jobId ||
    !/^[a-f0-9]{64}$/.test(plan.manifestHash ?? "") ||
    !Number.isSafeInteger(plan.expectedUnits) || plan.expectedUnits < 1) return false;
  return local?.status === "COMPLETED" && local?.protocolVersion === "CITIZEN_MONITOR_V1"
    && local?.confirmationSource === "MONITOR_COUNTER" && local?.monitorConfirmed === true
    && local?.localCompletionDurable === true && local?.attendanceComplete === true
    && local?.attendanceId === session?.attendanceId && local?.eventId === session?.eventId
    && local?.jobId === plan?.jobId && local?.manifestHash === plan?.manifestHash
    && local?.expectedUnits === plan?.expectedUnits && local?.confirmedUnits === plan?.expectedUnits;
}
export function isPhysicallyCompleted(local, remote) {
  return (
    local?.status === "COMPLETED" &&
    local?.physicallyConfirmed === true &&
    local?.receiptAcknowledged === true &&
    remote?.status === "COMPLETED" &&
    remote?.codeStatus === "CONSUMED"
  );
}
